/**
 * AuditLogTab Component
 * 
 * Admin panel tab for viewing permission audit log
 * 
 * Features:
 * - Filterable table with date range, user, action type, project
 * - Expandable rows showing full JSON details
 * - Export to CSV
 * 
 * Created: 2026-01-26 (Permission System V2 Phase 3)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

// Action type labels for display
const ACTION_TYPE_LABELS = {
  role_assigned: 'Role Assigned',
  role_removed: 'Role Removed',
  primary_role_updated: 'Primary Role Updated',
  secondary_role_added: 'Secondary Role Added',
  secondary_role_removed: 'Secondary Role Removed',
  project_assigned: 'Project Assigned',
  project_unassigned: 'Project Unassigned',
  project_role_override_updated: 'Project Role Override',
  trades_updated: 'Trades Updated',
  trade_added: 'Trade Added',
  trade_removed: 'Trade Removed',
  override_created: 'Override Created',
  override_updated: 'Override Updated',
  override_removed: 'Override Removed',
  overrides_expired_bulk: 'Overrides Expired (Bulk)',
  user_provisioned: 'User Provisioned',
  system_migration: 'System Migration'
};

const AuditLogTab = () => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  
  // Data state
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [filterTargetUser, setFilterTargetUser] = useState('');
  const [filterPerformedBy, setFilterPerformedBy] = useState('');
  const [filterActionType, setFilterActionType] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  
  // Load all entries (no pagination - scrollbar handles it)
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Exporting state
  const [exporting, setExporting] = useState(false);

  // Fetch action types and users for filters
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [actionTypesRes, usersRes, projectsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/audit-log/action-types'),
        authenticatedApiCall(instance, accounts, 'GET', '/users/all'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects')
      ]);
      
      if (actionTypesRes.data.success) {
        setActionTypes(actionTypesRes.data.data || []);
      }
      if (usersRes.data.success) {
        setUsers(usersRes.data.data || []);
      }
      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, [instance, accounts]);

  // Fetch audit log entries
  const fetchAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (filterTargetUser) params.append('target_user_id', filterTargetUser);
      if (filterPerformedBy) params.append('performed_by', filterPerformedBy);
      if (filterActionType) params.append('action_type', filterActionType);
      if (filterProject) params.append('project_id', filterProject);
      if (filterFromDate) params.append('from_date', filterFromDate);
      if (filterToDate) params.append('to_date', filterToDate);
      params.append('limit', 10000);
      
      const response = await authenticatedApiCall(
        instance, 
        accounts, 
        'GET', 
        `/audit-log?${params.toString()}`
      );
      
      if (response.data.success) {
        setEntries(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, filterTargetUser, filterPerformedBy, filterActionType, filterProject, filterFromDate, filterToDate]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  // Handle filter apply
  const handleApplyFilters = () => {
    fetchAuditLog();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilterTargetUser('');
    setFilterPerformedBy('');
    setFilterActionType('');
    setFilterProject('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  // Handle row expand toggle
  const toggleRowExpand = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Handle export
  const handleExport = async () => {
    if (!filterFromDate || !filterToDate) {
      showToast('Please select a date range for export', 'warning');
      return;
    }
    
    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      if (filterTargetUser) params.append('target_user_id', filterTargetUser);
      if (filterPerformedBy) params.append('performed_by', filterPerformedBy);
      if (filterActionType) params.append('action_type', filterActionType);
      if (filterProject) params.append('project_id', filterProject);
      params.append('from_date', filterFromDate);
      params.append('to_date', filterToDate);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/audit-log/export?${params.toString()}`,
        null,
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${filterFromDate}-to-${filterToDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      showToast('Audit log exported successfully', 'success');
    } catch (err) {
      console.error('Error exporting audit log:', err);
      showToast('Failed to export audit log', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Get action type label
  const getActionTypeLabel = (actionType) => {
    return ACTION_TYPE_LABELS[actionType] || actionType;
  };

  // Get action type color
  const getActionTypeColor = (actionType) => {
    if (actionType?.includes('removed') || actionType?.includes('expired')) {
      return '#dc3545'; // Red
    }
    if (actionType?.includes('created') || actionType?.includes('added') || actionType?.includes('assigned')) {
      return '#28a745'; // Green
    }
    if (actionType?.includes('updated')) {
      return '#007bff'; // Blue
    }
    return '#6c757d'; // Gray
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Permission Audit Log</h3>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          View history of all permission-related changes in the system.
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        {/* Target User Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
            Affected User
          </label>
          <select
            value={filterTargetUser}
            onChange={(e) => setFilterTargetUser(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.display_name || user.email}
              </option>
            ))}
          </select>
        </div>

        {/* Performed By Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
            Performed By
          </label>
          <select
            value={filterPerformedBy}
            onChange={(e) => setFilterPerformedBy(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Admins</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.display_name || user.email}
              </option>
            ))}
          </select>
        </div>

        {/* Action Type Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
            Action Type
          </label>
          <select
            value={filterActionType}
            onChange={(e) => setFilterActionType(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Actions</option>
            {actionTypes.map(type => (
              <option key={type} value={type}>
                {getActionTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>

        {/* Project Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
            Project
          </label>
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.job_number} - {project.job_name}
              </option>
            ))}
          </select>
        </div>

        {/* From Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
            From Date
          </label>
          <input
            type="date"
            value={filterFromDate}
            onChange={(e) => setFilterFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>

        {/* To Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
            To Date
          </label>
          <input
            type="date"
            value={filterToDate}
            onChange={(e) => setFilterToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>

      {/* Filter Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          onClick={handleApplyFilters}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Apply Filters
        </button>
        <button
          onClick={handleClearFilters}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Clear
        </button>
        <button
          onClick={handleExport}
          disabled={exporting || (!filterFromDate || !filterToDate)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: exporting ? '#6c757d' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: exporting ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            marginLeft: 'auto'
          }}
          title={!filterFromDate || !filterToDate ? 'Select date range to export' : ''}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Loading audit log...
        </div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          No audit log entries found.
        </div>
      ) : (
        <>
          {/* Table */}
          <div style={{ overflowY: 'scroll', overflowX: 'auto', height: '320px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '30px', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}></th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Date/Time</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Action</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Affected User</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Performed By</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Project</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>Role</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <React.Fragment key={entry.id}>
                    <tr 
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleRowExpand(entry.id)}
                    >
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        <span style={{ 
                          display: 'inline-block',
                          transform: expandedRows.has(entry.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}>
                          ▶
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap' }}>
                        {formatDate(entry.created_at)}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: getActionTypeColor(entry.action_type) + '20',
                          color: getActionTypeColor(entry.action_type),
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          fontWeight: 500
                        }}>
                          {getActionTypeLabel(entry.action_type)}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        {entry.target_user_name || entry.target_user_email || '-'}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        {entry.performed_by_name || entry.performed_by_email || 'System'}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        {entry.job_number ? `${entry.job_number}` : '-'}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                        {entry.role_id || '-'}
                      </td>
                    </tr>
                    {/* Expanded Details Row */}
                    {expandedRows.has(entry.id) && (
                      <tr>
                        <td colSpan={7} style={{ 
                          padding: '1rem', 
                          backgroundColor: '#f8f9fa',
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          <div style={{ fontSize: '0.85rem' }}>
                            <strong>Details:</strong>
                            <pre style={{
                              backgroundColor: '#fff',
                              padding: '1rem',
                              borderRadius: '4px',
                              overflow: 'auto',
                              maxHeight: '200px',
                              margin: '0.5rem 0 0 0'
                            }}>
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                            {entry.ip_address && (
                              <p style={{ margin: '0.5rem 0 0 0' }}>
                                <strong>IP Address:</strong> {entry.ip_address}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#666' }}>
            {entries.length} entries
          </div>
        </>
      )}
    </div>
  );
};

export default AuditLogTab;
